import Head from 'next/head'
import Page from '../../components/Page'
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import CommonHeadTags from '../../components/CommonHeadTags'
import {
  GoogleAnalyticsHead,
  GoogleAnalyticsBody,
} from '../../components/GoogleAnalytics'
import useTranslation from '../../translations/useTranslation'
import {
  nonEnglishLocales,
  isLocale,
  locales,
  defaultLocale,
  localeHreflangs,
} from '../../translations/config'
import { LocaleProvider } from '../../translations/context'
import pages from '../../routes'
import LocalizedEditorialPage from '../../components/LocalizedEditorialPage'
import getEditorialPage from '../../translations/getEditorialPage'

function PageRoute({ locale, page, editorialPage, editorialReviewedLabel }) {
  const { t } = useTranslation(locale)
  if (page === 'about' || page === 'contact') {
    return <LocalizedEditorialPage locale={locale} slug={page} page={editorialPage} reviewedLabel={editorialReviewedLabel} />
  }
  let RenderedPage = null
  let instrument = null

  switch (page) {
    case 'privacy-policy':
      RenderedPage = () => <PrivacyPolicy />
      break
    default:
      if (page.includes('tuning')) {
        instrument = page.split('-tuning')[0]
        RenderedPage = (
          <Page
            instrument={instrument}
            locale={locale}
            pageData={page}
          />
        )
      } else {
        RenderedPage = <Page pageData='home' />
      }
  }

  const title = instrument ? t(`title-${instrument}`) : t('description')
  const description = instrument ? t(`description-${instrument}`) : t('description')
  const canonicalUrl = locale === 'en'
    ? `https://guitar-tuner.org/${page}`
    : `https://guitar-tuner.org/${locale}/${page}`

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='description' content={description} />
        <meta httpEquiv='content-language' content={localeHreflangs[locale]} />
        {locale === 'en' ? (
          <link rel='canonical' href={`https://guitar-tuner.org/${page}`} />
        ) : (
          <link
            rel='canonical'
            href={`https://guitar-tuner.org/${locale}/${page}`}
          />
        )}
        <link
          rel='alternate'
          hrefLang='x-default'
          href={`https://guitar-tuner.org/${page}`}
        />
        <link
          rel='alternate'
          hrefLang='en'
          href={`https://guitar-tuner.org/${page}`}
        />
        {nonEnglishLocales.map((locale) => (
          <link
            key={locale}
            rel='alternate'
            hrefLang={localeHreflangs[locale]}
            href={`https://guitar-tuner.org/${locale}/${page}`}
          />
        ))}
        <CommonHeadTags
          title={title}
          description={description}
          url={canonicalUrl}
        />
        <GoogleAnalyticsHead />
      </Head>

      <LocaleProvider lang={locale || defaultLocale}>
        <Header instrument={instrument}/>

        <main>
          {RenderedPage}
        </main>
        <Footer />
      </LocaleProvider>
      <GoogleAnalyticsBody />
    </>
  )
}

export async function getStaticProps(ctx) {
  if (typeof ctx.params.lang !== 'string' || !isLocale(ctx.params.lang)) {
    // in case the value of 'lang' is not a valid locale return it as undefined
    return { props: { locale: undefined, page: ctx.params.page } }
  }

  // the locale is valid
  const editorialProps = ['about', 'contact'].includes(ctx.params.page)
    ? getEditorialPage(ctx.params.lang, ctx.params.page)
    : {}
  return { props: { locale: ctx.params.lang, page: ctx.params.page, ...editorialProps } }
}

export async function getStaticPaths() {
  let paths = []
  locales.forEach((locale) => {
    pages.forEach((page) => {
      paths.push({
        params: { lang: locale, page },
      })
    })
  })

  return {
    paths,
    fallback: false,
  }
}

export default PageRoute
